import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type FormEvent } from 'react';
import { api, ApiError, fieldIssues } from '../api';
import { can, type Me } from '../auth/me';
import { sameValues, useUnsavedGuard } from '../ui/unsaved';
import { TASKS_KEY } from './types';
import './tasks.css';
import { Notice } from '../ui/Notice';

/** "Ustawienia → Powiadomienia": automatic repeats of the task notifications (owner 2026-10-24 01:38). */
export const REPEAT_SETTINGS_RESOURCE = 'company_tasks.settings';
export type RepeatSettingsView = { unreceivedHours: number | null; unansweredHours: number | null; maxRepeats: number; quietFrom: string; quietTo: string; version: number };
const KEY = [...TASKS_KEY, 'repeat-settings'];
export const HOURS = [1, 2, 4, 8, 24] as const;

type Draft = { unreceivedHours: string; unansweredHours: string; maxRepeats: string; quietFrom: string; quietTo: string };
const draftOf = (s: RepeatSettingsView): Draft => ({
  unreceivedHours: s.unreceivedHours === null ? '' : String(s.unreceivedHours),
  unansweredHours: s.unansweredHours === null ? '' : String(s.unansweredHours),
  maxRepeats: String(s.maxRepeats),
  quietFrom: s.quietFrom,
  quietTo: s.quietTo,
});
/** The body the server takes ('' = off). */
export const bodyOf = (d: Draft) => ({
  unreceivedHours: d.unreceivedHours ? Number(d.unreceivedHours) : null,
  unansweredHours: d.unansweredHours ? Number(d.unansweredHours) : null,
  maxRepeats: Number(d.maxRepeats),
  quietFrom: d.quietFrom,
  quietTo: d.quietTo,
});

function HoursSelect({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <label className="field">
      <span>{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">Wyłączone</option>
        {HOURS.map((h) => (
          <option key={h} value={String(h)}>
            {h} godz.
          </option>
        ))}
      </select>
    </label>
  );
}

export function RepeatSettings({ me }: { me: Me }) {
  const queryClient = useQueryClient();
  const current = useQuery({ queryKey: KEY, queryFn: () => api<RepeatSettingsView>('/api/company-tasks/reminder-settings') }).data;
  const [draft, setDraft] = useState<Draft | null>(null);
  const [saved, setSaved] = useState(false);
  useEffect(() => {
    if (current) setDraft(draftOf(current));
  }, [current]);
  const canEdit = can(me, REPEAT_SETTINGS_RESOURCE, 'edit');
  const save = useMutation({
    mutationFn: (d: Draft) => api<RepeatSettingsView>('/api/company-tasks/reminder-settings', { method: 'PATCH', body: { version: current!.version, ...bodyOf(d) } }),
    onSuccess: (next) => {
      queryClient.setQueryData(KEY, next);
      setSaved(true);
    },
  });
  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => {
    setDraft((d) => (d ? { ...d, [k]: v } : d));
    setSaved(false);
  };
  const guardRef = useUnsavedGuard({
    dirty: canEdit && !!current && !!draft && !sameValues(draft, draftOf(current)),
    save: async () => {
      if (!draft) return false;
      await save.mutateAsync(draft);
      return true;
    },
  });
  function submit(e: FormEvent) {
    e.preventDefault();
    e.stopPropagation();
    if (draft) save.mutate(draft);
  }
  const error = save.error instanceof ApiError ? save.error : null;
  return (
    <section className="settings-section" aria-label="Powiadomienia" data-feature-id="company-tasks.repeat-settings">
      <h2>Powiadomienia</h2>
      <p className="hint">Ponowne przypomnienia o zadaniach firmowych: gdy osoba nie odebrała powiadomienia albo odebrała, ale nie odpowiedziała („Przyjmuję” albo zrobione).</p>
      <form ref={guardRef} className="settings-form plain" aria-label="Ponowne przypomnienia o zadaniach" onSubmit={submit}>
        <fieldset disabled={!canEdit || !draft}>
          {draft && (
            <>
              <HoursSelect label="Przypomnij ponownie, jeśli nieodebrane, co" value={draft.unreceivedHours} onChange={(v) => set('unreceivedHours', v)} />
              <HoursSelect label="Przypomnij, jeśli odebrane, ale bez odpowiedzi, po" value={draft.unansweredHours} onChange={(v) => set('unansweredHours', v)} />
              <label className="field">
                <span>Najwyżej tyle razy</span>
                <input type="number" min={1} max={10} step={1} value={draft.maxRepeats} onChange={(e) => set('maxRepeats', e.target.value)} />
              </label>
              <div className="ct-row">
                <label className="field">
                  <span>Cisza nocna od</span>
                  <input type="time" value={draft.quietFrom} onChange={(e) => set('quietFrom', e.target.value)} />
                </label>
                <label className="field">
                  <span>do</span>
                  <input type="time" value={draft.quietTo} onChange={(e) => set('quietTo', e.target.value)} />
                </label>
              </div>
              <p className="hint field-hint">W ciszy nocnej ponowne przypomnienia czekają do rana. „Przypomnij teraz” działa zawsze.</p>
            </>
          )}
          {error && (
            <p className="alert" role="alert">
              {error.status === 409 ? 'Ktoś właśnie zmienił ustawienia. Odśwież stronę.' : fieldIssues(error).map((i) => i.message).join(' ') || error.message}
            </p>
          )}
          {saved && (
            <Notice onClose={() => setSaved(false)}>
              Zapisano.
            </Notice>
          )}
          {canEdit && (
            <div className="actions">
              <button className="btn primary" type="submit" disabled={save.isPending}>
                {save.isPending ? 'Zapisywanie…' : 'Zapisz powiadomienia'}
              </button>
            </div>
          )}
        </fieldset>
      </form>
    </section>
  );
}
